import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Car, MessagesSquare, LayoutDashboard } from 'lucide-react';
import { shouldShowAutoDemoSwitcher } from './AutoDemoSwitcher';
import { demoSlot } from '../lib/session';

// Presenter-only switcher for the automotive 3-way demo. Opens each persona in
// its OWN tab via the deterministic entry routes (all share the same backend
// demo org). Self-hides unless the automotive demo flag is set. Not a dashboard,
// not an admin surface, exposes no credentials.
export default function AutoShowroomSwitcher() {
    const { t } = useTranslation();
    const { pathname, search } = useLocation();
    if (typeof window === 'undefined') return null;
    if (sessionStorage.getItem('yulontap_auto_demo') !== '1') return null;
    // Never inside a split-screen pane — the outer switcher is in charge there.
    if (demoSlot()) return null;
    // Coexistence guard: hide whenever the AutoDemoSwitcher is active so the two
    // bottom-center controls never render simultaneously.
    if (shouldShowAutoDemoSwitcher(pathname, search)) return null;

    const open = (path) => window.open(path, '_blank', 'noopener');
    const items = [
        { path: '/demo/automotive/buyer', icon: <Car className="w-4 h-4" />, label: t('demoAuto.switchBuyer', 'Buyer'), tid: 'auto-switch-buyer' },
        { path: '/demo/automotive/salesperson', icon: <MessagesSquare className="w-4 h-4" />, label: t('demoAuto.switchSalesperson', 'Salesperson'), tid: 'auto-switch-salesperson' },
        { path: '/demo/automotive/command-center', icon: <LayoutDashboard className="w-4 h-4" />, label: t('demoAuto.switchCommandCenter', 'Command Center'), tid: 'auto-switch-command-center' },
    ];

    return (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-1 rounded-full border border-amber-500/30 bg-neutral-900/90 backdrop-blur px-1.5 py-1.5 shadow-xl"
            data-testid="auto-showroom-switcher">
            <span className="px-2 text-[10px] uppercase tracking-widest text-amber-400/80 hidden sm:inline">{t('demoAuto.demoBadge', 'Live Demo')}</span>
            {items.map((it) => (
                <button key={it.path} onClick={() => open(it.path)} data-testid={it.tid}
                    className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-neutral-200 hover:bg-amber-500/15 hover:text-amber-300 transition-colors">
                    {it.icon}<span className="hidden sm:inline">{it.label}</span>
                </button>
            ))}
        </div>
    );
}
